import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Grid,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Devices as DevicesIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { DeviceAdminDetail } from 'src/api/Api';
import {
  DeviceTypeResponse,
  DeviceTypeCreateRequest,
  createDeviceType,
} from 'src/api2/deviceCustodyApi';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';

// AI-GENERATED-START
const CONDITION_COLOR: Record<string, string> = {
  NORMAL: '#00E676',
  DAMAGED: '#FF5252',
  MAINTENANCE: '#FFD600',
  MISSING: '#FF5252',
  RETIRED: '#666666',
};

const getConditionColor = (cond: string): string => {
  return CONDITION_COLOR[cond?.toUpperCase()] || '#AAAAAA';
};


interface AddDeviceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (device: DeviceAdminDetail) => void;
  onDeviceTypeCreated?: (deviceType: DeviceTypeResponse) => void;
  deviceTypes: DeviceTypeResponse[];
  deviceConditions: string[];
}

// ─── Shared input styles ─────────────────────────────────────────────────────
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#151515',
    color: '#FFF',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#2A2A2A' },
    '&:hover fieldset': { borderColor: '#444' },
    '&.Mui-focused fieldset': { borderColor: '#00E676' },
  },
  '& .MuiFormHelperText-root': { color: '#FF5252', mt: 0.5 },
};

// ─── Tab panel wrapper ────────────────────────────────────────────────────────
interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}
const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      overflow: 'hidden',
    }}
  >
    {value === index && (
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
        {children}
      </Box>
    )}
  </Box>
);

// ─── Add Device Form ─────────────────────────────────────────────────────────
interface AddDeviceFormProps {
  deviceTypes: DeviceTypeResponse[];
  deviceConditions: string[];
  onClose: () => void;
  onSuccess: (device: DeviceAdminDetail) => void;
}

const AddDeviceForm: FC<AddDeviceFormProps> = ({
  deviceTypes,
  deviceConditions,
  onClose,
  onSuccess,
}) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    device_type_id: '',
    name: '',
    purchased_at: null as string | null,
    condition: 'NORMAL',
  });
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    setFormData({
      device_type_id: deviceTypes[0]?.device_type_id || '',
      name: '',
      purchased_at: null,
      condition: deviceConditions[0] || 'NORMAL',
    });
    setErrors({});
  }, [deviceTypes, deviceConditions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Device name is required';
    if (!formData.device_type_id) newErrors.device_type_id = 'Device type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        device_type_id: formData.device_type_id,
        name: formData.name,
        purchased_at: formData.purchased_at
          ? new Date(formData.purchased_at).toISOString()
          : null,
        condition: formData.condition || 'NORMAL',
      };
      const response = await clientService.api.createApiDeviceApiV1AdminDevicesPost(
        payload as any,
      );
      onSuccess(response.data as DeviceAdminDetail);
      showSnackbar({ message: 'Device added successfully!', type: SnackbarType.SUCCESS });
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.detail?.[0]?.msg || error?.message || 'Failed to add device';
      showSnackbar({ message: errorMessage, type: SnackbarType.ERROR });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogContent
        sx={{
          p: 3,
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {/* Device Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
            Device Name *
          </Typography>
          <TextField
            fullWidth
            name="name"
            placeholder="e.g. Amazon Rainforest Audio Node"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={INPUT_SX}
          />
        </Box>

        {/* Device Type + Purchased At */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
              Device Type *
            </Typography>
            <TextField
              select
              fullWidth
              name="device_type_id"
              value={formData.device_type_id}
              onChange={handleChange}
              error={!!errors.device_type_id}
              helperText={errors.device_type_id}
              SelectProps={{
                IconComponent: KeyboardArrowDownIcon,
                MenuProps: {
                  PaperProps: {
                    sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' },
                  },
                },
              }}
              sx={INPUT_SX}
            >
              {deviceTypes.map(type => (
                <MenuItem key={type.device_type_id} value={type.device_type_id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
              Purchased At
            </Typography>
            <TextField
              fullWidth
              type="date"
              name="purchased_at"
              value={formData.purchased_at || ''}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ color: '#444', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                ...INPUT_SX,
                '& .MuiOutlinedInput-root': {
                  ...INPUT_SX['& .MuiOutlinedInput-root'],
                  '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Condition */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
            Condition
          </Typography>
          <TextField
            select
            fullWidth
            name="condition"
            value={formData.condition}
            onChange={handleChange}
            SelectProps={{
              IconComponent: KeyboardArrowDownIcon,
              MenuProps: {
                PaperProps: {
                  sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' },
                },
              },
            }}
            sx={INPUT_SX}
          >
            {deviceConditions.map(cond => (
              <MenuItem key={cond} value={cond}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getConditionColor(cond),
                    }}
                  />
                  {cond.charAt(0) + cond.slice(1).toLowerCase()}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>

      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          borderTop: '1px solid #1E1E1E',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: '#AAA',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: '#FFF', bgcolor: 'transparent' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />
          }
          sx={{
            bgcolor: '#00E676',
            color: '#000',
            px: 4,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': { bgcolor: '#00c853' },
            '&:disabled': { bgcolor: 'rgba(0,230,118,0.3)', color: '#000' },
          }}
        >
          {loading ? 'Adding...' : 'Add Device'}
        </Button>
      </Box>
    </>
  );
};

// ─── Add Device Type Form ─────────────────────────────────────────────────────
interface AddDeviceTypeFormProps {
  onClose: () => void;
  onDeviceTypeCreated?: (deviceType: DeviceTypeResponse) => void;
}

const AddDeviceTypeForm: FC<AddDeviceTypeFormProps> = ({ onClose, onDeviceTypeCreated }) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    prefix: '',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Auto-uppercase prefix as user types
    let processed = value;
    if (name === 'prefix') {
      processed = value.toUpperCase().replace(/[^A-Z]/g, '');
    } else if (name === 'category') {
      // Auto-uppercase and keep alphanumeric/underscores for clean category key names
      processed = value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    }
    setFormData(prev => ({ ...prev, [name]: processed }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Type name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.prefix.trim()) {
      newErrors.prefix = 'Prefix is required';
    } else if (formData.prefix.length > 6) {
      newErrors.prefix = 'Prefix must be at most 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload: DeviceTypeCreateRequest = {
        name: formData.name.trim(),
        category: formData.category,
        prefix: formData.prefix.trim(),
        description: formData.description.trim() || undefined,
        is_active: true,
      };
      const created = await createDeviceType(payload);
      onDeviceTypeCreated?.(created);
      showSnackbar({ message: `Device type "${created.name}" created!`, type: SnackbarType.SUCCESS });
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.detail ||
        error?.message ||
        'Failed to create device type';
      showSnackbar({ message: errorMessage, type: SnackbarType.ERROR });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogContent
        sx={{
          p: 3,
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {/* Info banner */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'rgba(0,230,118,0.05)',
            border: '1px solid rgba(0,230,118,0.15)',
            borderRadius: '8px',
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(0,230,118,0.8)', lineHeight: 1.6 }}>
            Device types define the category and serial-number prefix for a class of devices.
            Once created, they appear immediately in the "Add Device" form.
          </Typography>
        </Box>

        {/* Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
            Type Name *
          </Typography>
          <TextField
            fullWidth
            name="name"
            placeholder="e.g. AudioMoth v1.2"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={INPUT_SX}
          />
        </Box>

        {/* Category + Prefix */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={7}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
              Category *
            </Typography>
            <TextField
              fullWidth
              name="category"
              placeholder="e.g. AUDIO, CAMERA, SENSOR, CLIMATE"
              value={formData.category}
              onChange={handleChange}
              error={!!errors.category}
              helperText={errors.category}
              sx={INPUT_SX}
            />
            {/* Quick Suggestion Chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
              {['AUDIO', 'CAMERA', 'SENSOR', 'WEATHER', 'CLIMATE'].map(cat => (
                <Chip
                  key={cat}
                  label={cat}
                  size="small"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, category: cat }));
                    if (errors.category) setErrors(prev => ({ ...prev, category: null }));
                  }}
                  sx={{
                    bgcolor: formData.category === cat ? 'rgba(0, 230, 118, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                    color: formData.category === cat ? '#00E676' : 'rgba(255, 255, 255, 0.5)',
                    border: `1px solid ${formData.category === cat ? 'rgba(0, 230, 118, 0.3)' : 'rgba(255, 255, 255, 0.05)'}`,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: formData.category === cat ? 'rgba(0, 230, 118, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
              Prefix *{' '}
              <Typography component="span" variant="caption" sx={{ color: '#555' }}>
                (up to 6 letters)
              </Typography>
            </Typography>
            <TextField
              fullWidth
              name="prefix"
              placeholder="e.g. AUD"
              value={formData.prefix}
              onChange={handleChange}
              error={!!errors.prefix}
              helperText={errors.prefix || 'Used in serial numbers'}
              inputProps={{ maxLength: 6, style: { letterSpacing: '0.1em', fontWeight: 700 } }}
              sx={INPUT_SX}
            />
          </Grid>
        </Grid>

        {/* Preview */}
        {formData.prefix && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#555' }}>
              Serial number preview:
            </Typography>
            <Chip
              label={`${formData.prefix}-000001`}
              size="small"
              sx={{
                bgcolor: 'rgba(0,230,118,0.08)',
                color: '#00E676',
                fontFamily: 'monospace',
                fontWeight: 700,
                fontSize: '11px',
                border: '1px solid rgba(0,230,118,0.2)',
              }}
            />
          </Box>
        )}

        {/* Description */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>
            Description{' '}
            <Typography component="span" variant="caption" sx={{ color: '#555' }}>
              (optional)
            </Typography>
          </Typography>
          <TextField
            fullWidth
            name="description"
            placeholder="Brief description of this device type..."
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            sx={INPUT_SX}
          />
        </Box>
      </DialogContent>

      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          borderTop: '1px solid #1E1E1E',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: '#AAA',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: '#FFF', bgcolor: 'transparent' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />
          }
          sx={{
            bgcolor: '#00E676',
            color: '#000',
            px: 4,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': { bgcolor: '#00c853' },
            '&:disabled': { bgcolor: 'rgba(0,230,118,0.3)', color: '#000' },
          }}
        >
          {loading ? 'Creating...' : 'Create Device Type'}
        </Button>
      </Box>
    </>
  );
};

// ─── Main dialog ──────────────────────────────────────────────────────────────
const AddDevicePage: FC<AddDeviceDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onDeviceTypeCreated,
  deviceTypes,
  deviceConditions,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // Reset tab when dialog opens
  useEffect(() => {
    if (open) setActiveTab(0);
  }, [open]);

  const TAB_STYLES = (isActive: boolean) => ({
    textTransform: 'none' as const,
    fontWeight: isActive ? 700 : 500,
    color: isActive ? '#00E676' : '#666',
    fontSize: '13px',
    minHeight: 42,
    gap: 0.75,
    '&:hover': { color: isActive ? '#00E676' : '#AAA' },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: '#111',
          color: '#FFF',
          borderRadius: '16px',
          maxWidth: '520px',
          width: '100%',
          border: '1px solid #1E1E1E',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '18px' }}>
            {activeTab === 0 ? 'Add New Device' : 'Add Device Type'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#555' }}>
            {activeTab === 0
              ? 'Register a physical device to the platform.'
              : 'Define a new category of device for the fleet.'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#444', mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 3, borderBottom: '1px solid #1E1E1E' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: '#00E676', height: 2 } }}
          sx={{ minHeight: 42 }}
        >
          <Tab
            icon={<DevicesIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Add Device"
            id="tab-add-device"
            aria-controls="tabpanel-add-device"
            sx={TAB_STYLES(activeTab === 0)}
          />
          <Tab
            icon={<CategoryIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Add Device Type"
            id="tab-add-device-type"
            aria-controls="tabpanel-add-device-type"
            sx={TAB_STYLES(activeTab === 1)}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <AddDeviceForm
          deviceTypes={deviceTypes}
          deviceConditions={deviceConditions}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <AddDeviceTypeForm
          onClose={onClose}
          onDeviceTypeCreated={onDeviceTypeCreated}
        />
      </TabPanel>
    </Dialog>
  );
};
// AI-GENERATED-END

export default AddDevicePage;